/**
 * Protocole applicatif — source de vérité unique pour le client et le serveur.
 *
 * Deux protocoles distincts cohabitent ici :
 *
 * 1. **Signaling** (client ↔ serveur, JSON sur WebSocket) : mise en relation des pairs.
 *    Le serveur lit ces messages ; il doit donc les valider avec méfiance.
 *
 * 2. **Canal de données** (pair ↔ pair, JSON scellé dans des trames binaires) : métadonnées
 *    et accusés de réception du transfert. Le serveur ne voit jamais ces messages en clair.
 *
 * Toute évolution du protocole se fait ici, jamais dans un seul des deux camps.
 */

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Types de base
// ─────────────────────────────────────────────────────────────────────────────

export const RoleSchema = z.enum(['sender', 'receiver']);
export type Role = z.infer<typeof RoleSchema>;

export const PeerStateSchema = z.enum(['ready', 'busy', 'done', 'cancelled']);
export type PeerState = z.infer<typeof PeerStateSchema>;

export const PeerInfoSchema = z.object({
  id: z.string().min(1).max(64),
  role: RoleSchema,
  name: z.string().max(64).optional(),
});
export type PeerInfo = z.infer<typeof PeerInfoSchema>;

/**
 * Codes d'erreur du signaling.
 *
 * `relay_disabled` existe dès le MVP alors que le relais n'est pas implémenté : le serveur
 * répond ce code au lieu de tomber sur une branche non gérée. C'est ce qui permet à la
 * Phase 4 d'ajouter le relais sans changer le contrat.
 */
export const ErrorCodeSchema = z.enum([
  'room_full',
  'role_taken',
  'rate_limited',
  'relay_disabled',
  /**
   * Le lien a déjà servi, et la salle était à usage unique.
   *
   * Distinct de `room_full` à dessein : « ce partage est en cours avec quelqu'un d'autre »
   * et « ce lien a déjà été utilisé, demandez-en un nouveau » appellent des gestes
   * différents de la part de l'utilisateur. Les confondre le laisserait réessayer en vain.
   */
  'room_consumed',
  'payload_too_large',
  'bad_message',
  'unsupported_version',
  'internal',
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

/**
 * Politique d'usage d'une salle.
 *
 * `one-shot` : la salle se ferme définitivement dès que le destinataire la quitte. Le
 * lien meurt avec elle — même pour celui qui vient de l'utiliser.
 *
 * `multi` : comportement historique, la salle reste ouverte jusqu'à son expiration.
 *
 * Le lien **est** le secret du partage : un lien qui fuit (historique de navigateur,
 * presse-papiers, conversation) reste sinon exploitable jusqu'à l'expiration de la salle.
 * L'usage unique borne cette fenêtre à une seule utilisation.
 */
export const RoomPolicySchema = z.enum(['one-shot', 'multi']);
export type RoomPolicy = z.infer<typeof RoomPolicySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Signaling — charge utile SDP / ICE
// ─────────────────────────────────────────────────────────────────────────────

/** Description de session, telle qu'échangée entre les pairs. */
export const SdpDescriptionSchema = z.object({
  type: z.enum(['offer', 'answer', 'pranswer', 'rollback']),
  sdp: z
    .string()
    .max(64 * 1024)
    .optional(),
});
export type SdpDescription = z.infer<typeof SdpDescriptionSchema>;

export const SdpPayloadSchema = z.object({
  kind: z.literal('sdp'),
  description: SdpDescriptionSchema,
});

/**
 * Candidat ICE transporté sur le fil.
 *
 * Le champ `candidate` est **obligatoire** ici, alors qu'il est optionnel dans
 * `RTCIceCandidateInit`. C'est délibéré : sur le fil, un candidat absent n'a pas de sens.
 * La fin de collecte est signalée par `candidate: null` au niveau du payload, pas par
 * un objet vide. Confondre les deux rendrait le protocole ambigu.
 */
export const IceCandidateSchema = z.object({
  candidate: z.string().min(1).max(2048),
  sdpMid: z.string().max(64).nullish(),
  sdpMLineIndex: z.number().int().nullish(),
  usernameFragment: z.string().max(256).nullish(),
});
export type IceCandidatePayload = z.infer<typeof IceCandidateSchema>;

/**
 * `candidate: null` signale la fin des candidats (fin de gathering).
 * Le destinataire appelle alors `addIceCandidate()` sans argument.
 */
export const IcePayloadSchema = z.object({
  kind: z.literal('ice'),
  candidate: IceCandidateSchema.nullable(),
});

export const SignalPayloadSchema = z.discriminatedUnion('kind', [
  SdpPayloadSchema,
  IcePayloadSchema,
]);
export type SignalPayload = z.infer<typeof SignalPayloadSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Signaling — messages
// ─────────────────────────────────────────────────────────────────────────────

export const ClientMsgSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('hello'),
    room: z.string().min(1).max(64),
    role: RoleSchema,
    name: z.string().max(64).optional(),
    version: z.number().int().optional(),
    /**
     * Politique d'usage de la salle, annoncée par l'émetteur.
     *
     * Optionnel au niveau du schéma, et **son absence vaut `one-shot` côté serveur**.
     * C'est un choix fail-closed : un client antérieur à cette version n'envoie rien, et
     * un champ optionnel dont l'absence signifierait « multi » rendrait la protection
     * contournable en omettant simplement le champ. Le défaut sûr est le défaut fermé.
     */
    policy: RoomPolicySchema.optional(),
  }),
  z.object({ t: z.literal('signal'), payload: SignalPayloadSchema }),
  z.object({
    t: z.literal('relay'),
    seq: z.number().int().nonnegative(),
    data: z.string(),
  }),
  z.object({ t: z.literal('relay-ack'), seq: z.number().int().nonnegative() }),
  z.object({ t: z.literal('peer-state'), state: PeerStateSchema }),
  z.object({ t: z.literal('bye'), reason: z.string().max(200).optional() }),
  z.object({ t: z.literal('ping'), ts: z.number() }),
]);
export type ClientMsg = z.infer<typeof ClientMsgSchema>;

export const ServerMsgSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('welcome'),
    room: z.string(),
    selfId: z.string(),
    peers: z.array(PeerInfoSchema),
    version: z.number().int(),
  }),
  z.object({ t: z.literal('peer-join'), peer: PeerInfoSchema }),
  z.object({ t: z.literal('peer-left'), peerId: z.string() }),
  z.object({ t: z.literal('signal'), from: z.string(), payload: SignalPayloadSchema }),
  z.object({
    t: z.literal('relay'),
    from: z.string(),
    seq: z.number().int().nonnegative(),
    data: z.string(),
  }),
  z.object({ t: z.literal('relay-ack'), seq: z.number().int().nonnegative() }),
  z.object({ t: z.literal('peer-state'), from: z.string(), state: PeerStateSchema }),
  z.object({ t: z.literal('error'), code: ErrorCodeSchema, message: z.string() }),
  z.object({ t: z.literal('pong'), ts: z.number() }),
]);
export type ServerMsg = z.infer<typeof ServerMsgSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Canal de données — métadonnées et contrôle
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Métadonnées d'un fichier. Transmises **scellées** (AES-GCM) : le serveur de relais
 * ne doit pas pouvoir lire les noms de fichiers. Sur un chemin P2P pur cette protection
 * est redondante avec DTLS, mais elle garantit qu'aucun chemin ne fuit ces informations.
 */
export const FileMetaSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(1024),
  size: z.number().int().nonnegative(),
  mime: z.string().max(255),
  lastModified: z.number().int().nonnegative(),
});
export type FileMeta = z.infer<typeof FileMetaSchema>;

/**
 * Motif d'un refus, transmis sous forme de **code** et non de phrase.
 *
 * Les deux navigateurs peuvent être dans des langues différentes : envoyer du texte
 * aboutirait à afficher une phrase française chez un utilisateur anglophone. Un code
 * laisse chaque côté formuler le message dans sa propre langue.
 */
export const RejectReasonSchema = z.enum([
  /** Le destinataire a refusé, sans autre précision. */
  'declined',
  /** Il n'a pas pu choisir où enregistrer le fichier. */
  'destination-unavailable',
  /** Son navigateur ne peut pas recevoir un fichier de cette taille. */
  'cannot-receive',
]);
export type RejectReason = z.infer<typeof RejectReasonSchema>;

export const DcControlSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('meta'), files: z.array(FileMetaSchema).min(1).max(1000) }),
  z.object({ t: z.literal('accept') }),
  z.object({ t: z.literal('reject'), reason: RejectReasonSchema }),
  /**
   * Reprise après coupure (§6.6).
   *
   * Le récepteur annonce, pour chaque fichier, les plages de morceaux qui lui manquent.
   * L'émetteur reprend à ces offsets au lieu de renvoyer le fichier entier.
   *
   * Les plages sont exprimées en **indices de morceaux**, pas en octets : c'est l'unité
   * dans laquelle les deux côtés découpent déjà, et cela évite toute erreur d'arrondi sur
   * des fichiers dont la taille n'est pas un multiple de `CHUNK_SIZE`.
   *
   * Un fichier absent de la liste est à envoyer **entièrement** : c'est le cas nominal
   * d'un premier transfert, où le récepteur n'a rien à demander de particulier.
   */
  z.object({
    t: z.literal('resume-request'),
    files: z
      .array(
        z.object({
          fileIndex: z.number().int().nonnegative(),
          /**
           * Identité du fichier source tel que le récepteur l'a mémorisé.
           *
           * L'émetteur la compare à son propre fichier avant d'accepter. Sans elle, il
           * ne pourrait pas savoir si les morceaux déjà reçus proviennent bien de la
           * version courante du fichier. Reprendre sur un fichier modifié entre-temps
           * produirait un fichier **silencieusement corrompu** — le pire résultat
           * possible pour un outil de transfert.
           */
          source: z.object({
            name: z.string().min(1).max(1024),
            size: z.number().int().nonnegative(),
            lastModified: z.number().int().nonnegative(),
          }),
          ranges: z.array(
            z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]),
          ),
        }),
      )
      .max(1000),
  }),
  /** L'émetteur confirme qu'il accepte de reprendre à ces plages. */
  z.object({ t: z.literal('resume-accepted') }),
  z.object({
    t: z.literal('file-done'),
    fileIndex: z.number().int().nonnegative(),
    sha256: z.string().length(64),
  }),
  z.object({ t: z.literal('all-done') }),
  z.object({ t: z.literal('cancel') }),
  z.object({ t: z.literal('error'), message: z.string().max(500) }),
]);
export type DcControl = z.infer<typeof DcControlSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Configuration ICE
// ─────────────────────────────────────────────────────────────────────────────

export const IceServerSchema = z.object({
  urls: z.union([z.string(), z.array(z.string())]),
  username: z.string().optional(),
  credential: z.string().optional(),
});
export type IceServer = z.infer<typeof IceServerSchema>;

export const IceConfigSchema = z.object({
  iceServers: z.array(IceServerSchema),
  ttl: z.number().int().nonnegative().optional(),
  /**
   * Le serveur accepte-t-il de relayer les trames quand le direct échoue ?
   *
   * Annoncé dès la récupération de la configuration ICE — donc **avant** toute
   * négociation — pour que le client sache s'il doit tenter une bascule ou échouer
   * franchement. Sans cette information, un échec ICE sur un serveur sans relais
   * déclencherait une tentative vouée à se solder par `relay_disabled`.
   *
   * Optionnel : un serveur antérieur à la Phase 4 ne le renseigne pas. L'absence vaut
   * « pas de relais », ce qui reproduit exactement le comportement du MVP.
   */
  relay: z.boolean().optional(),
});
export type IceConfig = z.infer<typeof IceConfigSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Décodage tolérant aux pannes
// ─────────────────────────────────────────────────────────────────────────────

export type DecodeResult<T> = { ok: true; value: T } | { ok: false; error: string };

function decodeWith<T>(schema: z.ZodType<T>, raw: string): DecodeResult<T> {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'JSON invalide' };
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const path = first?.path.join('.') ?? '?';
    return { ok: false, error: `${path}: ${first?.message ?? 'message invalide'}` };
  }
  return { ok: true, value: parsed.data };
}

/**
 * Décode un message client. Ne lève jamais : une entrée hostile produit un résultat
 * d'erreur que l'appelant transforme en `error{bad_message}`. Un serveur de signaling
 * est exposé à Internet — il ne doit pas pouvoir être arrêté par un `JSON.parse` raté.
 */
export function decodeClientMsg(raw: string): DecodeResult<ClientMsg> {
  return decodeWith(ClientMsgSchema, raw);
}

/** Décode un message serveur côté client. Même contrat de non-levée. */
export function decodeServerMsg(raw: string): DecodeResult<ServerMsg> {
  return decodeWith(ServerMsgSchema, raw);
}

/** Décode un message de contrôle du canal de données (après déchiffrement). */
export function decodeDcControl(raw: string): DecodeResult<DcControl> {
  return decodeWith(DcControlSchema, raw);
}
